import format from "date-fns/format";
import max from "date-fns/max";
import _, { sortBy } from "lodash";

import { handleComplianceStatus } from "@features/orders";

import {
  camelCaseKeys,
  displayId,
  formatDateString,
  stringToCents,
  utcDate,
} from "../../../utility/utilityFunctions";
import {
  handleImages,
  handleVariants,
  mapSpecifications,
} from "../../mapHelpers";

const orderTypeMap = {
  "pre-order": "Pre-Order",
  "from-inventory": "Inventory",
  "on-demand": "On-Demand",
  "to-inventory": "Restock",
};

const mapAddress = (address) =>
  _.pick(camelCaseKeys(address), [
    "id",
    "name",
    "city",
    "state",
    "country",
    "streetAddress1",
    "streetAddress2",
    "zip",
  ]);

const monthFromDateString = (dateString) =>
  dateString && format(utcDate(dateString), "MMMM");

export const mapSingleOrder = (order) => {
  const orderVariants = mapOrderVariants(order["order-variants"], "history");
  const formattedOrder = {
    id: order.id,
    user: order.user.name,
    distributorAbn: order.address ? order.address.abn : null,
    address: order.address ? mapAddress(order.address) : null,
    attn: order.attn
      ? order.attn
      : order.address?.["current-user-attn"]
        ? order.address["current-user-attn"]
        : order.address?.["default-attn"]
          ? order.address["default-attn"]
          : "---",
    program:
      order["program-names"] && order["program-names"].length > 0
        ? order["program-names"].join(", ")
        : "---",
    orderType: order.type,
    orderingType: order["ordering-type"],
    type: order["order-window-name"] ?? orderTypeMap[order["ordering-type"]],
    brand: [
      ...new Set(
        [].concat.apply(
          [],
          order["order-variants"].map((variant) =>
            variant.variant.item.brands.map((brand) => brand.name)
          )
        )
      ),
    ],
    businessUnit: [
      ...new Set(
        [].concat.apply(
          [],
          order["order-variants"].map((variant) =>
            variant.variant.item.brands.map(
              (brand) => brand["business-unit"].name
            )
          )
        )
      ),
    ],
    variants: orderVariants,
    orderAnaplanSubCodes: [
      ...new Set(
        orderVariants.flatMap((variant) => variant.anaplanSubProgramCodes)
      ),
    ],
    hasCanceledOrDestroyedVariants:
      orderVariants.filter(
        (ov) => ov.isCanceled || ov.isComplianceCanceled || ov.isDestroyed
      ).length > 0,
    status:
      order.status === "submitted"
        ? "Submitted - Pending Approval"
        : order.status,

    orderDate: order["submitted-at"]
      ? format(new Date(order["submitted-at"]), "MM/dd/yyyy")
      : "---",
    approvedDate: order["approved-at"]
      ? format(new Date(order["approved-at"]), "MM/dd/yyyy")
      : "---",
    shipDate:
      orderVariants.length > 0 &&
      orderVariants.filter((variant) => variant.actShipDate === "---")
        .length === 0
        ? format(
            max(orderVariants.map((o) => new Date(o.actShipDate))),
            "MM/dd/yyyy"
          )
        : "---",
    totalQty: order["total-quantity"],
    totalProductCost:
      order.type === "from-inventory" // don't show item cost for from-inventory orders
        ? 0
        : stringToCents(
            +order["total-actual-cost"] === 0
              ? +order["total-estimated-cost"]
              : +order["total-actual-cost"]
          ),

    totalEstFreight: stringToCents(order["total-estimated-shipping-cost"]),
    totalActFreight: stringToCents(order["total-actual-shipping-cost"]),
    totalEstTax: stringToCents(order["total-estimated-tax"]),
    totalActTax: stringToCents(order["total-actual-tax"]),
    totalActCost: stringToCents(order["total-actual-cost"]),
    totalEstExtendedCost: stringToCents(
      +order["total-estimated-shipping-cost"] +
        +order["total-estimated-tax"] +
        +order["total-beacon-cost"] +
        (order.type === "from-inventory" // don't show item cost for from-inventory orders
          ? 0
          : +order["total-actual-cost"] === 0
            ? +order["total-estimated-cost"]
            : +order["total-actual-cost"])
    ),
    totalActExtendedCost: stringToCents(
      +order["total-actual-shipping-cost"] +
        +order["total-actual-tax"] +
        +order["total-beacon-cost"] +
        +order["total-actual-cost"]
    ),

    totalActualCost: order["total-actual-cost"],
    totalActualShippingCost: order["total-actual-shipping-cost"],
    totalBeaconCost: order["total-beacon-cost"],
    totalEstimatedCost: order["total-estimated-cost"],
    totalEstimatedShippingCost: order["total-estimated-shipping-cost"],
    totalEstimatedTax: order["total-estimated-tax"],

    billableTotalActualCost: order["billable-total-actual-cost"],
    billableTotalActualShippingCost:
      order["billable-total-actual-shipping-cost"],
    billableTotalBeaconCost: order["billable-total-beacon-cost"],
    billableTotalEstimatedCost: order["billable-total-estimated-cost"],
    billableTotalEstimatedShippingCost:
      order["billable-total-estimated-shipping-cost"],
    billableTotalEstimatedTax: order["billable-total-estimated-tax"],

    note: order.notes ? order.notes : "---",
    marketName: order["market-name"],
    marketId: order["market-id"],
    isForDistributor: order["is-for-distributor"],
    territoryName: order["territory-name"] ? order["territory-name"] : "---",
    territoryId: order["territory-id"],
    subStateName: order["sub-state"]?.name,
    subStateId: order["sub-state"]?.id,
    toInventoryAllocation:
      "Restock: " +
      (order["sub-state"]
        ? order["sub-state"].name
        : order.territory
          ? order["territory-name"]
          : "National"),
    approvalLogging: order["approval-logging"] ?? "---",
    anaplanProgramTypes: order["anaplan-program-types"] ?? [],
    approvalError: order["approval-error"],
  };
  return formattedOrder;
};

export const mapOrderVariants = (variants, type) => {
  const mappedVariants = variants
    .map((variant) => {
      const images = handleImages(
        variant.variant.item.images,
        variant.variant.image
      );
      const itemNumber =
        variant["item-number"] || variant.variant["variant-sku"];
      const rn = variant["run-number"];
      return {
        id: variant.id,
        itemId: variant.variant.item.id,
        variantId: variant.variant.id,
        itemNumber,
        displayId: displayId(itemNumber, rn),
        imgUrlThumb: images.imgUrlThumb,
        imgUrlLg: images.imgUrlLg,
        brand:
          variant.variant.item.brands.length > 0
            ? variant.variant.item.brands.map((brand) => brand.name).join(", ")
            : "---",
        specification: variant.variant.item.specification
          ? mapSpecifications(
              variant.variant.item.specification,
              "item-display"
            )
          : null,
        brandCode: variant.variant.item.brands
          .map((brand) => brand["external-id"])
          .join(", "),
        program: variant["program-names"]
          ? variant["program-names"].join(", ")
          : variant.variant.item.programs.map((prog) => prog.name).join(", "),
        programs: sortBy(variant.variant.item.programs, "start-date")
          .reverse()
          .map((prog) => ({
            id: prog.id,
            name: prog.name,
          })),
        itemType: variant.variant.item.type,
        itemDescription: variant.variant.item.comment
          ? variant.variant.item.comment
          : "---",
        unit:
          variant.variant.item.brands.length > 0
            ? [
                ...new Set(
                  variant.variant.item.brands.map(
                    (brand) => brand["business-unit"].name
                  )
                ),
              ].join(", ")
            : "---",
        packSize: variant.variant.item["qty-per-pack"],
        leadTime: variant.variant.item["lead-time-in-days"],
        supplierId: variant.variant.item.supplier.id,
        state:
          type === "order-set-variant"
            ? "---"
            : variant.order.address
              ? variant.order.address.state
              : "---",
        estCost: stringToCents(variant["estimated-cost"]),
        totalQty: type === "order-set-variant" ? 0 : variant.qty,
        totalEstShipping:
          type === "order-set-variant"
            ? null
            : stringToCents(variant["total-estimated-shipping-cost"]),
        totalEstTax:
          type === "order-set-variant"
            ? null
            : stringToCents(variant["total-estimated-tax"]),
        totalEstCost:
          type === "order-set-variant"
            ? 0
            : stringToCents(variant["total-estimated-cost"]),
        totalActCost: stringToCents(variant["total-actual-cost"]),
        complianceStatus:
          type !== "order-set-variant" &&
          variant["triggered-rules"].length > 0 &&
          variant["triggered-rules"].filter(
            (rule) =>
              (rule.type === "item-type" ||
                rule.type === "metal-wood" ||
                rule.type === "coupon-item-type" ||
                rule.type === "coupon-offer-type" ||
                rule.type === "coupon-face-vale") &&
              rule.status !== "approved"
          ).length > 0
            ? "not-compliant"
            : "compliant",
        complianceDisplay:
          type !== "order-set-variant"
            ? variant["overrides-compliance"]
              ? "Compliance Overridden"
              : handleComplianceStatus([
                  ...variant["triggered-rules"],
                  ...variant["prior-approval-triggered-rules"],
                ])
            : null,
        overridesCompliance: !!variant["overrides-compliance"],
        overriddenAt: variant["overridden-at"] ?? null,
        overrideReason: variant["override-reason"] ?? null,
        overriddenByName: variant["overridden-by-name"] ?? null,
        priorApprovalDenied:
          type !== "order-set-variant" && variant["prior-approval-denied"]
            ? variant["prior-approval-denied"]
            : false,
        isComplianceCanceled:
          variant.status === "canceled" &&
          variant["cancelation-type"] === "compliance",
        isCanceled:
          variant.status === "canceled" &&
          variant["cancelation-type"] === "order",
        canceledNote:
          variant.status === "canceled" && variant["cancelation-note"]
            ? variant["cancelation-note"]
            : null,
        isDestroyed: variant.status === "destroyed",
        orderType: variant["order-type"],
        itemOrderType: variant.variant.item["item-order-type"],
        removeFromCatalogDate: variant.variant.item["remove-from-catalog-date"],
        estimatedInMarketDate: variant["estimated-in-market-date"]
          ? variant["estimated-in-market-date"]
          : "---",
        inMarketDate: variant["in-market-date"]
          ? formatDateString(variant["in-market-date"])
          : variant["estimated-in-market-date"] && type === "order-set-variant"
            ? variant["estimated-in-market-date"]
            : "---",
        inMarketMonth:
          monthFromDateString(variant["in-market-date"]) ??
          monthFromDateString(variant["po-in-market-date"]) ??
          "---",
        isRush: variant["is-rush"] ? variant["is-rush"] : false,
        estShipDate: variant["po-in-market-date"]
          ? formatDateString(variant["po-in-market-date"])
          : null,
        poId: variant["po-id"] ? variant["po-id"] : "---",
        actShipDate:
          variant["shipping-parameter-variant"] &&
          variant["shipping-parameter-variant"]["actual-ship-date"]
            ? formatDateString(
                variant["shipping-parameter-variant"]["actual-ship-date"]
              )
            : variant["actual-ship-date"]
              ? formatDateString(variant["actual-ship-date"])
              : "---",
        carrier:
          variant["tracking-data"] && variant["tracking-data"].carrier
            ? variant["tracking-data"].carrier
            : "---",
        tracking:
          variant["tracking-data"] && variant["tracking-data"].number
            ? variant["tracking-data"].number
            : "---",
        isTransferred: variant["is-transferred"],
        onShipHold: variant["shipping-parameter-variant"]
          ? variant["shipping-parameter-variant"]["compliance-status"] ===
              "prior-approval-pending" ||
            variant["shipping-parameter-variant"]["compliance-status"] ===
              "denied"
          : "---",
        isCoupon: variant.variant.item["is-coupon"],
        couponInfo: variant.variant.item["coupon-custom-raw-payload"]
          ? {
              startDate: formatDateString(
                variant.variant.item["coupon-custom-raw-payload"]
                  .CouponStartDate
              ),
              expirationDate: formatDateString(
                variant.variant.item["coupon-custom-raw-payload"]
                  .CouponExpirationDate
              ),
              typeCode:
                variant.variant.item["coupon-custom-raw-payload"]
                  .CouponTypeCode,
              offerType:
                variant.variant.item["coupon-custom-raw-payload"]
                  .CouponOfferTypeCode,
              description:
                variant.variant.item["coupon-custom-raw-payload"]
                  .CouponOfferDescription,
              bottles:
                variant.variant.item["coupon-custom-raw-payload"].Quantity,
              bottleDiscount:
                variant.variant.item["coupon-custom-raw-payload"]
                  .CouponFaceValue * 100,
            }
          : null,
        couponStartDate: variant.variant.item["coupon-issue-date"]
          ? formatDateString(variant.variant.item["coupon-issue-date"])
          : null,
        couponExpirationDate: variant.variant.item["coupon-expiration-date"]
          ? formatDateString(variant.variant.item["coupon-expiration-date"])
          : null,
        couponOfferType: variant.variant.item["coupon-offer-type-code"]
          ? variant.variant.item["coupon-offer-type-code"]
          : null,
        couponOfferDescription: variant.variant.item["coupon-offer-description"]
          ? variant.variant.item["coupon-offer-description"]
          : null,
        couponTypeCode: variant.variant.item["coupon-type-code"]
          ? variant.variant.item["coupon-type-code"]
          : null,
        variantName: handleVariants([variant.variant])[0].name,
        moq: variant.moq ?? "---",
        includeBeacon: variant.variant.item["include-beacon"],
        mostRecentProgramId: variant.variant.item["most-recent-program-id"],
        mostRecentBeaconCost: variant.variant.item["most-recent-beacon-cost"],
        anaplanSubProgramCodes: variant["anaplan-sub-program-codes"],

        totalActualCost: variant["total-actual-cost"],
        totalActualShippingCost: variant["total-actual-shipping-cost"],
        totalBeaconCost: variant["total-beacon-cost"],
        totalEstimatedCost: variant["total-estimated-cost"],
        totalEstimatedShippingCost: variant["total-estimated-shipping-cost"],
        totalEstimatedTax: variant["total-estimated-tax"],

        billableTotalActualCost: variant["billable-total-actual-cost"],
        billableTotalActualShippingCost:
          variant["billable-total-actual-shipping-cost"],
        billableTotalBeaconCost: variant["billable-total-beacon-cost"],
        billableTotalEstimatedCost: variant["billable-total-estimated-cost"],
        billableTotalEstimatedShippingCost:
          variant["billable-total-estimated-shipping-cost"],
        billableTotalEstimatedTax: variant["billable-total-estimated-tax"],

        // Fields needed by getDistributorOrderVariantCosts
        qty: variant.qty,
        estimatedCost: variant["estimated-cost"],
        marketId: variant["market-id"],
        orderOrderingType: variant["order-ordering-type"],
        distributorApprovedCost: variant["distributor-approved-cost"],
        distributorApprovedShippingCost:
          variant["distributor-approved-shipping-cost"],
        distributorApprovedTax: variant["distributor-approved-tax"],
        distributorUnitProductCost: variant["distributor-unit-product-cost"],
        distributorTotalProductCost: variant["distributor-total-product-cost"],
      };
    })
    .sort((a, b) => {
      return parseInt(a.variantId) < parseInt(b.variantId)
        ? -1
        : parseInt(a.variantId) > parseInt(b.variantId)
          ? 1
          : 0;
    });
  return mappedVariants;
};
